import * as React from "react";
import { Formik } from "formik";
import { RouteComponentProps, withRouter } from "react-router";

import { AssistantServices } from "@/services";
import { validationSchema } from "./validations";
import { AssistantForm } from "@/components/AssistantForm";
import { InternalGrid } from "@/styling/baseStyle";
import { toAssistantPermissions, useSocialCoachSession } from "@/utils";
import { SegmentationDetail, Assistant } from "@/interfaces";
import { useMemo } from "react";

interface OwnProps {
  assistant?: Assistant;
  formSubmitRef?: any;
  withWritePermission?: boolean;

  onChangeHandler: () => void;
  successHandler: () => void;
  errorHandler: () => void;
}

type Props = OwnProps & RouteComponentProps;

const AssistantEditFormFC: React.FC<Props> = ({
  assistant,
  formSubmitRef,
  onChangeHandler,
  successHandler,
  withWritePermission,
  errorHandler,
}) => {
  const { coachId } = useSocialCoachSession();

  const permissionsMap = useMemo(() => {
    return (
      assistant?.account?.permissions?.reduce((m, p) => {
        return { ...m, [p.componentId.toString()]: p.level };
      }, {}) || {}
    );
  }, [assistant?.account?.permissions]);

  const branchIds = useMemo(() => {
    return assistant?.segmentationDetails?.filter(d => d.type == "BRANCH")?.map(d => d.segmentationDetailId) || [];
  }, [assistant?.segmentationDetails]);

  const regionIds = useMemo(() => {
    return assistant?.segmentationDetails?.filter(d => d.type == "REGION")?.map(d => d.segmentationDetailId) || [];
  }, [assistant?.segmentationDetails]);

  return (
    <InternalGrid>
      <Formik
        validateOnBlur={false}
        validateOnChange={true}
        initialValues={{
          userId: assistant?.userId,
          firstName: assistant?.account?.profile?.firstName,
          lastName: assistant?.account?.profile?.lastName,
          email: assistant?.account?.user?.email,
          branches: branchIds,
          states: [],
          regions: regionIds,
          permissionsMap: permissionsMap,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, setErrors, validateForm }) => {
          const { firstName, lastName, email, regions, branches } = values;

          try {
            const errors = await validateForm({
              firstName,
              lastName,
              email,
            });

            if (errors && Object.values(errors).length > 0) {
              setErrors(errors);
              errorHandler();
            } else {
              const permissionsArray = toAssistantPermissions(values.permissionsMap);
              const branchesToSave = branches.map(branchId => {
                return {
                  segmentationDetailId: Number(branchId),
                  coachId,
                  type: "BRANCH",
                } as SegmentationDetail;
              });

              const regionsToSave = regions.map(branchId => {
                return {
                  segmentationDetailId: Number(branchId),
                  coachId,
                  type: "REGION",
                } as SegmentationDetail;
              });

              AssistantServices.updateAssistant(assistant!.userId, {
                account: {
                  firstName,
                  lastName,
                  email: assistant?.account?.user?.email,
                  permissions: permissionsArray,
                },
                segmentation: branchesToSave.concat(regionsToSave),
              }).then(() => {
                alert("User updated successfully");
                successHandler();
              });
            }
          } catch (e) {
            setErrors({
              email: `${e}`,
              firstName: undefined,
              lastName: undefined,
            });
            errorHandler();
          }
          setSubmitting(false);
        }}
      >
        {({ values, errors, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
          return (
            <AssistantForm
              messages={{ error: Object.values(errors) as string[] }}
              coachId={coachId!}
              handleChange={(e, data) => {
                handleChange(e);
                onChangeHandler();
              }}
              handleSubmit={handleSubmit}
              formSubmitRef={formSubmitRef}
              handleSetFieldValue={(fieldName, data) => {
                setFieldValue(fieldName, data);
                onChangeHandler();
              }}
              latestLoginDate={assistant?.account?.user?.latestLoginDate}
              createdDate={assistant?.account?.user?.createdDate}
              isSubmitting={isSubmitting}
              withWritePermission={withWritePermission}
              values={values}
            />
          );
        }}
      </Formik>
    </InternalGrid>
  );
};

export const AssistantEditForm = withRouter(AssistantEditFormFC);
